import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { FiscalProfileEditorValues } from "@/lib/fiscalProfileEditorTypes";

export interface FiscalAutomationsSectionProps {
  idPrefix: string;
  /** Defaults to 'owner'. 'accountant' swaps "tu" copy for third-person. */
  audience?: "owner" | "accountant";
}

/**
 * Opt-in to the monthly "pedido de boleto de BPS" (FiscalAutoRequestCronService,
 * 09:00 Uruguay on the 1st). Lives on the SHARED editor, so the accountant
 * cartera tab gets it as well as the SMB settings page.
 *
 * DELIBERATELY NOT PLAN-GATED, unlike SettingsFiscalAutomationCard which is
 * Ultra-only. The tenants that actually receive these emails are on pro / tala /
 * literal E — copying that card's `isUltraPlan` gate would render this toggle to
 * nobody who needs it and ship the opt-in unreachable (spec 3d339756 FM-2).
 *
 * Titled "Automatizaciones de BPS", not "Automatizaciones": on an Ultra tenant's
 * settings page this card renders directly above SettingsFiscalAutomationCard
 * ("Automatizaciones fiscales", the IVA boleto), and two cards with the same
 * heading reads as one of them being a duplicate.
 *
 * The one thing it does follow is the editor's own agenda gate: on the free
 * plan there is no fiscal agenda and therefore no BPS obligation for the cron
 * to find, so the toggle would promise an automation that cannot run.
 */
export function FiscalAutomationsSection({
  idPrefix,
  audience = "owner",
}: FiscalAutomationsSectionProps): JSX.Element {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<FiscalProfileEditorValues>();
  const enabled = watch("autoRequestBpsBoleto");
  const audienceKey = audience === "accountant" ? "Acct" : "";

  const checkboxId = `${idPrefix}-auto-request-bps-boleto`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-foreground">
          {t(
            "settings.fiscalProfile.sections.automationsTitle",
            "Automatizaciones de BPS",
          )}
        </CardTitle>
        <CardDescription>
          {t(
            `settings.fiscalProfile.sections.automationsDescription${audienceKey}`,
            audience === "accountant"
              ? "Lo que Fiscalo puede hacer solo con los aportes de BPS de este cliente. Desactivado salvo que lo actives."
              : "Lo que Fiscalo puede hacer solo con tus aportes de BPS. Desactivado salvo que lo actives.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-3">
          <Checkbox
            id={checkboxId}
            checked={enabled}
            onCheckedChange={(v) =>
              setValue("autoRequestBpsBoleto", v === true, {
                shouldDirty: true,
              })
            }
            className="mt-0.5 transition-colors duration-150"
          />
          <div className="flex-1 space-y-1">
            <Label
              htmlFor={checkboxId}
              className="cursor-pointer text-sm font-medium"
            >
              {t(
                "settings.fiscalProfile.autoRequestBpsBoleto",
                "Pedir el boleto de BPS automáticamente",
              )}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t(
                `settings.fiscalProfile.autoRequestBpsBoletoHelp${audienceKey}`,
                audience === "accountant"
                  ? "El 1° de cada mes le enviamos un email al contador del cliente pidiéndole el boleto de pago de BPS del período que vence ese mes. Desactivado, no se envía nada."
                  : "El 1° de cada mes le enviamos un email a tu contador pidiéndole el boleto de pago de BPS del período que vence ese mes. Desactivado, no se envía nada.",
              )}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
